import { useMemo, useState } from "react";
import { addDays, format, isAfter, isBefore, isSameDay } from "date-fns";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";

interface DateSelectorProps {
  activeDate: Date;
  savedDates: Date[];
  onSelectDate: (date: Date) => void;
}

const DateSelector = ({
  activeDate,
  savedDates,
  onSelectDate,
}: DateSelectorProps) => {
  const today = useMemo(() => {
    const t = new Date();
    t.setHours(0, 0, 0, 0);
    return t;
  }, []);

  const maxSelectableDate = addDays(today, 21);

  // page start for 14-day window (2 rows x 7)
  const [pageStart, setPageStart] = useState<Date>(today);

  const days = useMemo(
    () => Array.from({ length: 14 }, (_, i) => addDays(pageStart, i)),
    [pageStart],
  );

  const isDisabled = (d: Date) =>
    isBefore(d, today) || isAfter(d, maxSelectableDate);
  const isSaved = (d: Date) => savedDates.some((x) => isSameDay(x, d));
  const isActive = (d: Date) => isSameDay(d, activeDate);

  const canGoPrev = !isSameDay(pageStart, today);
  const canGoNext = isBefore(
    addDays(pageStart, 14),
    addDays(maxSelectableDate, 1),
  );

  const prev = () => canGoPrev && setPageStart(addDays(pageStart, -14));
  const next = () => canGoNext && setPageStart(addDays(pageStart, 14));

  return (
    <div className="space-y-3">
      {/* header */}
      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={prev}
          disabled={!canGoPrev}
          className={cn(
            "p-2 rounded-lg transition",
            canGoPrev
              ? "hover:bg-secondary text-muted-foreground"
              : "opacity-30 cursor-not-allowed",
          )}
        >
          <ChevronLeft className="w-4 h-4" />
        </button>

        <p className="text-sm font-semibold text-foreground">
          {format(pageStart, "MMMM yyyy")}
        </p>

        <button
          type="button"
          onClick={next}
          disabled={!canGoNext}
          className={cn(
            "p-2 rounded-lg transition",
            canGoNext
              ? "hover:bg-secondary text-muted-foreground"
              : "opacity-30 cursor-not-allowed",
          )}
        >
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>

      {/* day labels (2 rows) */}
      <div className="grid grid-cols-7 gap-3">
        {days.slice(7, 14).map((d) => (
          <div
            key={"h2-" + d.toISOString()}
            className="text-center text-[11px] text-muted-foreground"
          >
            {format(d, "EEE").toUpperCase()}
          </div>
        ))}
      </div>

      {/* date buttons (2 rows) */}
      <div className="grid grid-cols-7 gap-3">
        {days.map((d) => {
          const disabled = isDisabled(d);
          const active = isActive(d);
          const saved = isSaved(d);

          return (
            <button
              key={d.toISOString()}
              type="button"
              disabled={disabled}
              onClick={() => !disabled && onSelectDate(d)}
              className={cn(
                "h-10 w-10 mx-auto rounded-full flex items-center justify-center text-sm font-medium transition",
                disabled && "opacity-30 cursor-not-allowed",
                active && "bg-[#FF6600] text-white",
                !active &&
                  saved &&
                  "border border-[#FF6600] text-[#101828] bg-white",
                !active &&
                  !saved &&
                  "border border-[#EAECF0] text-[#101828] bg-white hover:bg-[#F9FAFB]",
              )}
            >
              {format(d, "d")}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default DateSelector;
// import {
//   format,
//   addDays,
//   isSameDay,
//   isToday,
//   startOfMonth,
//   endOfMonth,
//   startOfWeek,
//   endOfWeek,
//   eachDayOfInterval,
//   isAfter,
//   isBefore,
// } from "date-fns";
// import { Check, ChevronLeft, ChevronRight } from "lucide-react";
// import { cn } from "@/lib/utils";
// import { useState } from "react";

// interface DateSelectorProps {
//   selectedDates: Date[];
//   onDateToggle: (date: Date) => void;
// }

// const DateSelector = ({ selectedDates, onDateToggle }: DateSelectorProps) => {
//   const today = new Date();
//   today.setHours(0, 0, 0, 0);

//   // ✅ Allow selection only within next 3 weeks
//   const maxSelectableDate = addDays(today, 21);

//   const [currentMonth, setCurrentMonth] = useState(today);

//   const monthStart = startOfMonth(currentMonth);
//   const monthEnd = endOfMonth(currentMonth);
//   const calendarStart = startOfWeek(monthStart);
//   const calendarEnd = endOfWeek(monthEnd);

//   const calendarDays = eachDayOfInterval({
//     start: calendarStart,
//     end: calendarEnd,
//   });

//   const isDateSelected = (date: Date) =>
//     selectedDates.some((d) => isSameDay(d, date));

//   // ✅ Updated disable logic
//   const isDateDisabled = (date: Date) => {
//     const dateOnly = new Date(date);
//     dateOnly.setHours(0, 0, 0, 0);

//     return (
//       isBefore(dateOnly, today) || // before today
//       isAfter(dateOnly, maxSelectableDate) // after 3 weeks
//     );
//   };

//   const isOutsideMonth = (date: Date) => {
//     return date.getMonth() !== currentMonth.getMonth();
//   };

//   const handlePrevMonth = () => {
//     setCurrentMonth((prev) => addDays(startOfMonth(prev), -1));
//   };

//   const handleNextMonth = () => {
//     setCurrentMonth((prev) => addDays(endOfMonth(prev), 1));
//   };

//   // dummy check uncheck for available whole day
//   const [checked, setChecked] = useState(false);

//   return (
//     <div className="space-y-3">
//       {/* Month/Year header */}
//       <div className="flex items-center justify-between">
//         <button
//           onClick={handlePrevMonth}
//           className="p-2 rounded-lg hover:bg-secondary transition-colors text-muted-foreground hover:text-foreground"
//           aria-label="Previous month"
//         >
//           <ChevronLeft className="w-4 h-4" />
//         </button>

//         <span className="text-sm font-semibold text-foreground">
//           {format(currentMonth, "MMMM yyyy")}
//         </span>

//         <button
//           onClick={handleNextMonth}
//           className="p-2 rounded-lg hover:bg-secondary transition-colors text-muted-foreground hover:text-foreground"
//           aria-label="Next month"
//         >
//           <ChevronRight className="w-4 h-4" />
//         </button>
//       </div>

//       {/* Day headers */}
//       <div className="grid grid-cols-7 gap-1">
//         {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((day) => (
//           <div
//             key={day}
//             className="text-center text-xs font-medium text-muted-foreground py-1"
//           >
//             {day}
//           </div>
//         ))}
//       </div>

//       {/* Calendar grid */}
//       <div className="grid grid-cols-7 gap-1">
//         {calendarDays.map((date) => {
//           const selected = isDateSelected(date);
//           const disabled = isDateDisabled(date);
//           const todayDate = isToday(date);
//           const outside = isOutsideMonth(date);

//           return (
//             <button
//               key={date.toISOString()}
//               onClick={() => !disabled && !outside && onDateToggle(date)}
//               disabled={disabled || outside}
//               className={cn(
//                 "relative w-9 h-9 mx-auto rounded-full flex items-center justify-center text-xs font-medium transition-all duration-200",
//                 outside && "text-muted-foreground/30 cursor-default",
//                 disabled &&
//                   !outside &&
//                   "text-muted-foreground/40 cursor-not-allowed",
//                 !disabled &&
//                   !outside &&
//                   !selected &&
//                   "text-foreground hover:bg-secondary",
//                 selected && "bg-accent text-accent-foreground",
//                 todayDate &&
//                   !selected &&
//                   !outside &&
//                   "ring-1 ring-accent text-accent",
//               )}
//               aria-label={format(date, "EEEE, MMMM d")}
//               aria-pressed={selected}
//             >
//               {format(date, "d")}
//             </button>
//           );
//         })}
//       </div>

//       <div className="flex justify-between items-center mt-6">
//         <div className="border border-[#E4E7EC] px-4 py-2.5 rounded-[32px]">
//           <p className="text-[#344054] text-sm tracking-[-1%]">
//             Mon, <span className="font-bold font-grotesk">8</span>th Jan
//           </p>
//         </div>

//         <div className="flex items-center gap-x-2">
//           <label className="cursor-pointer inline-flex items-center">
//             <input
//               type="checkbox"
//               checked={checked}
//               onChange={() => setChecked(!checked)}
//               className="hidden"
//             />

//             <div
//               className={`size-5 border flex items-center justify-center transition-all duration-200 rounded
//       ${checked ? "border-[#FF6600] bg-[#FF6600]/10" : "border-[#FF6600]"}`}
//             >
//               {checked && <Check size={14} className="text-[#FF6600]" strokeWidth={2} />}
//             </div>
//           </label>
//           <p className="text-[#475467] text-sm tracking-normal">
//             I’m available All-day
//           </p>
//         </div>
//       </div>

//       {selectedDates.length > 0 && (
//         <div className="text-xs text-muted-foreground text-center pt-1">
//           {selectedDates.length} date
//           {selectedDates.length > 1 ? "s" : ""} selected
//         </div>
//       )}
//     </div>
//   );
// };

// export default DateSelector;
